import type { TicketStatus, TicketPriority, SuggestionStatus, UserRole } from './api';

/**
 * Single source of truth for ticket status/priority presentation.
 *
 * Before this module, status/priority color maps were duplicated across
 * TicketList, TicketDetail, SuggestionsPage, PriorityQueuePage and UsersPage
 * (5 diverging definitions). Everything visual for a status/priority now lives
 * here: one semantic tone per *meaning*, one plain-language label.
 *
 * Each status maps to one tone. The four review/waiting statuses — code review,
 * ready to deploy, customer review, reporter review — each carry a DISTINCT tone
 * so they're tellable apart at a glance (ticket #293). The rest keep their
 * New / Active / Blocked / Done meanings.
 */

export type Tone =
  | 'neutral'
  | 'info'
  | 'active'
  | 'warn'
  | 'danger'
  | 'success'
  | 'ready'
  | 'customer'
  | 'reporter'
  | 'verify';

/** Tailwind classes for each semantic tone (badge background + text). */
export const toneClass: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-700',
  info: 'bg-blue-100 text-blue-800',
  active: 'bg-indigo-100 text-indigo-800',
  warn: 'bg-amber-100 text-amber-900',
  danger: 'bg-rose-100 text-rose-800',
  success: 'bg-emerald-100 text-emerald-800',
  // The four review/waiting statuses each own a distinct tone (ticket #293) so
  // they're tellable apart at a glance. PENDING_CODE_REVIEW keeps `warn` (amber).
  ready: 'bg-cyan-100 text-cyan-800',
  customer: 'bg-violet-100 text-violet-800',
  reporter: 'bg-fuchsia-100 text-fuchsia-800',
  // Reminder / Verification — a standalone tracking state, teal so it's distinct
  // from the dev-pipeline review tones above.
  verify: 'bg-teal-100 text-teal-800',
};

export const statusTone: Record<TicketStatus, Tone> = {
  OPEN: 'info',
  IN_PROGRESS: 'active',
  BLOCKED: 'danger',
  PENDING_CODE_REVIEW: 'warn',
  READY_TO_DEPLOY: 'ready',
  PENDING_CUSTOMER_REVIEW: 'customer',
  PENDING_REPORTER_REVIEW: 'reporter',
  PENDING_VERIFICATION: 'verify',
  // Parked for later — muted so it reads as "not in play".
  FUTURE: 'neutral',
  RESOLVED: 'success',
};

/** Plain-language status labels — no enum jargon in the UI. */
export const statusLabel: Record<TicketStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  BLOCKED: 'Blocked',
  PENDING_CODE_REVIEW: 'Code review',
  READY_TO_DEPLOY: 'Ready to deploy',
  PENDING_CUSTOMER_REVIEW: 'Customer review',
  PENDING_REPORTER_REVIEW: 'Reporter review',
  PENDING_VERIFICATION: 'Reminder / Verification',
  FUTURE: 'Future Tickets',
  RESOLVED: 'Resolved',
};

/**
 * Priority tone. LOW/NORMAL is the baseline and renders no badge at all
 * (information hiding) — callers should check `priorityHasBadge` before
 * rendering a <PriorityBadge>.
 */
export const priorityTone: Record<TicketPriority, Tone> = {
  LOW: 'neutral',
  NORMAL: 'neutral',
  HIGH: 'warn',
  URGENT: 'danger',
};

export const priorityLabel: Record<TicketPriority, string> = {
  LOW: 'Low',
  NORMAL: 'Normal',
  HIGH: 'High',
  URGENT: 'Urgent',
};

/** Only HIGH/URGENT earn a visible priority badge in dense lists. */
export function priorityHasBadge(priority: TicketPriority): boolean {
  return priority === 'HIGH' || priority === 'URGENT';
}

/**
 * True when a YYYY-MM-DD due date is today in the viewer's local timezone.
 * Staff are all in Mountain time, so local === the business day the API uses
 * for its due-today → URGENT rule. Used to render the due date in red.
 */
export function isDueToday(due: string | null | undefined): boolean {
  if (!due) return false;
  const now = new Date();
  const localToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
  return due.slice(0, 10) === localToday;
}

// Suggestion status — same one-source-of-truth treatment as ticket status.
export const suggestionStatusTone: Record<SuggestionStatus, Tone> = {
  OPEN: 'info',
  PLANNED: 'active',
  DONE: 'success',
  DECLINED: 'neutral',
};

export const suggestionStatusLabel: Record<SuggestionStatus, string> = {
  OPEN: 'Open',
  PLANNED: 'Planned',
  DONE: 'Done',
  DECLINED: 'Declined',
};

// User role tones for the admin Users table.
export const roleTone: Record<UserRole, Tone> = {
  ADMIN: 'active',
  DEVELOPER: 'success',
  STAFF: 'neutral',
};
